import { useMemo, useRef, useState } from "react"

import {
  getZodiacSlug,
  updateCurrentUserProfile,
  useUserProfile,
  type Gender,
  type MaritalStatus,
  type UserProfile,
} from "@/features/user"
import { showErrorToast } from "@/stores/toast-store"
import { cn } from "@/lib/utils"

import BackArrow from "@/assets/img/profile/back_arrow.svg?react"

interface EditProfileModalProps {
  onClose: () => void
  onSaved: () => void
}

type MaritalUi = "" | "single" | "married" | "divorced"

const MARITAL_OPTIONS: { value: MaritalUi; label: string }[] = [
  { value: "", label: "Not selected" },
  { value: "single", label: "Single" },
  { value: "married", label: "Married" },
  { value: "divorced", label: "Divorced" },
]

const GENDERS: Gender[] = ["male", "female", "other"]

/** Backend maritalStatus → the UI's 4 options. */
function maritalToUi(value: string): MaritalUi {
  const v = value.toLowerCase()
  if (v === "single" || v === "free") return "single"
  if (v === "relationship" || v === "married" || v === "soulmate") return "married"
  if (v === "difficult" || v === "divorced") return "divorced"
  return ""
}

/**
 * UI option → backend maritalStatus.
 * If the selection still maps to the same bucket as `/me`, keep the original
 * value so "soulmate" is not rewritten to "married" on an unrelated save.
 */
function maritalToApi(value: MaritalUi, original: MaritalStatus | null): MaritalStatus | null {
  if (value === "") return null
  if (original && maritalToUi(original) === value) return original
  if (value === "single") return "free"
  if (value === "divorced") return "difficult"
  return "married"
}

const pad = (n: number): string => String(n).padStart(2, "0")

/** Backend `HH:mm` → native `type="time"` value. */
function timeToInput(value: string | null | undefined): string {
  if (!value) return ""
  const match = value.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return ""
  return `${pad(Number(match[1]))}:${match[2]}`
}

function asGender(value: string | null | undefined): Gender | "" {
  if (value && GENDERS.includes(value as Gender)) return value as Gender
  return ""
}

function formFromProfile(profile: UserProfile | null) {
  return {
    name: profile?.name || "",
    gender: asGender(profile?.gender),
    dob: profile?.birthDate || "",
    marital: maritalToUi(profile?.maritalStatus || ""),
    time: timeToInput(profile?.birthTime),
  }
}

/** "YYYY-MM-DD" → "DD/MM/YYYY" (placeholder otherwise). */
function dateDisplay(dob: string): string {
  const [year, month, day] = dob.split("-")
  if (!year || !month || !day) return "MM/DD/YYYY"
  return `${day}/${month}/${year}`
}

/** 24h "HH:MM" → "HH:MM AM/PM" (placeholder otherwise). */
function timeDisplay(value: string): string {
  if (!value) return "--:--"
  const [h, m] = value.split(":").map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return "--:--"
  const period = h >= 12 ? "PM" : "AM"
  return `${pad(h % 12 || 12)}:${pad(m)} ${period}`
}

function openPicker(input: HTMLInputElement | null) {
  if (!input) return
  if ("showPicker" in input && typeof input.showPicker === "function") {
    input.showPicker()
    return
  }
  input.click()
  input.focus()
}

/** Edit-profile form: reads and writes only `/v1/users/me`. */
export function EditProfileModal({ onClose, onSaved }: EditProfileModalProps) {
  const { profile } = useUserProfile()

  if (!profile) {
    return (
      <EditProfileShell onClose={onClose}>
        <p className="px-4 py-8 text-center font-body text-sm text-purple-light/70">
          Loading profile…
        </p>
      </EditProfileShell>
    )
  }

  return <EditProfileForm key={profile.id} profile={profile} onClose={onClose} onSaved={onSaved} />
}

function EditProfileShell({
  onClose,
  children,
}: {
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-[1050] mx-auto flex max-h-[100dvh] w-full max-w-app flex-col overflow-y-auto bg-dark-base [scrollbar-width:none]">
      <div className="relative flex h-14 shrink-0 items-center justify-center">
        <button type="button" onClick={onClose} className="absolute left-4 [&_svg]:size-6">
          <BackArrow />
        </button>
        <h2 className="font-display text-[22px] font-medium text-purple-light">Edit my profile</h2>
      </div>
      {children}
    </div>
  )
}

function EditProfileForm({
  profile,
  onClose,
  onSaved,
}: EditProfileModalProps & { profile: UserProfile }) {
  const initial = useMemo(() => formFromProfile(profile), [profile])

  const [name, setName] = useState(initial.name)
  const [gender, setGender] = useState<Gender | "">(initial.gender)
  const [dob, setDob] = useState(initial.dob)
  const [marital, setMarital] = useState<MaritalUi>(initial.marital)
  const [time, setTime] = useState(initial.time)

  const [saving, setSaving] = useState(false)
  const [nameError, setNameError] = useState(false)
  const [dateError, setDateError] = useState(false)
  const [maritalOpen, setMaritalOpen] = useState(false)

  const dateRef = useRef<HTMLInputElement | null>(null)
  const timeRef = useRef<HTMLInputElement | null>(null)

  const hasChanges =
    name !== initial.name ||
    gender !== initial.gender ||
    dob !== initial.dob ||
    marital !== initial.marital ||
    time !== initial.time
  const canSave = hasChanges && Boolean(dob) && !saving

  const maritalLabel = MARITAL_OPTIONS.find((o) => o.value === marital)?.label ?? "Not selected"

  const handleSave = async () => {
    const nameOk = name.trim().length > 0
    const dateOk = Boolean(dob)
    if (!nameOk || !dateOk) {
      setNameError(!nameOk)
      setDateError(!dateOk)
      return
    }

    const nextGender = asGender(gender) || asGender(profile?.gender)
    const year = Number(dob.split("-")[0])
    const zodiacSign = getZodiacSlug(dob)
    if (!nextGender || !Number.isInteger(year) || !zodiacSign) return

    setSaving(true)
    try {
      await updateCurrentUserProfile({
        name: name.trim(),
        gender: nextGender,
        birthDate: dob,
        birthTime: timeToInput(time) || null,
        year,
        zodiacSign,
        maritalStatus: maritalToApi(marital, profile?.maritalStatus ?? null),
        contactEmail: profile?.contactEmail ?? null,
        birthPlace: profile?.birthPlace ?? null,
      })
      onSaved()
    } catch {
      showErrorToast("Couldn't save profile", "Please try again")
    } finally {
      setSaving(false)
    }
  }

  return (
    <EditProfileShell onClose={onClose}>
      <div className="flex flex-1 flex-col px-4 py-4">
        <div className="flex flex-1 flex-col gap-4">
          {/* Name */}
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (e.target.value.trim()) setNameError(false)
              }}
              className={cn(
                "h-12 w-full rounded-xl bg-purple-light/10 px-3 font-body text-base font-medium text-purple-light outline-none",
                nameError && "border border-peach",
              )}
            />
            {nameError && (
              <span className="font-body text-xs text-peach">Name required for readings</span>
            )}
          </Field>

          {/* Date + time of birth */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date of birth*">
              <button
                type="button"
                onClick={() => openPicker(dateRef.current)}
                className={cn(
                  "flex h-12 items-center justify-between rounded-xl bg-purple-light/10 px-4",
                  dateError && "border border-peach",
                )}
              >
                <span
                  className={cn(
                    "font-body text-base font-medium",
                    dob ? "text-purple-light" : "text-purple-light/20",
                  )}
                >
                  {dateDisplay(dob)}
                </span>
                <CalendarIcon />
              </button>
              {dateError && (
                <span className="font-body text-xs text-peach">Date required for readings</span>
              )}
            </Field>

            <Field label="Time of birth">
              <button
                type="button"
                onClick={() => openPicker(timeRef.current)}
                className="flex h-12 items-center justify-between rounded-xl bg-purple-light/10 px-3"
              >
                <span
                  className={cn(
                    "font-body text-base font-medium",
                    time ? "text-purple-light" : "text-purple-light/20",
                  )}
                >
                  {timeDisplay(time)}
                </span>
                <ClockIcon />
              </button>
            </Field>
          </div>

          {/* Gender */}
          <Field label="Gender">
            <div className="grid h-12 grid-cols-2 gap-0 rounded-xl border border-purple-light/20 p-1">
              {(["male", "female"] as const).map((value) => {
                const selected = gender === value
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setGender(value)}
                    className={cn(
                      "rounded-lg font-body text-base font-medium capitalize",
                      selected ? "bg-purple-light text-dark-base" : "text-purple-light",
                    )}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Marital status */}
          <Field label="Marital status">
            <div className="relative">
              <button
                type="button"
                onClick={() => setMaritalOpen((v) => !v)}
                className="flex h-12 w-full items-center justify-between rounded-xl bg-purple-light/10 px-3"
              >
                <span className="font-body text-base font-medium text-purple-light">
                  {maritalLabel}
                </span>
                <ChevronDownIcon open={maritalOpen} />
              </button>
              {maritalOpen && (
                <>
                  <button
                    type="button"
                    aria-hidden
                    className="fixed inset-0 z-[1] cursor-default"
                    onClick={() => setMaritalOpen(false)}
                  />
                  <div className="absolute inset-x-0 top-14 z-[2] flex flex-col overflow-hidden rounded-xl border border-purple-light/20 bg-dark-surface py-1">
                    {MARITAL_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setMarital(option.value)
                          setMaritalOpen(false)
                        }}
                        className={cn(
                          "px-4 py-2.5 text-left font-body text-base font-medium text-purple-light",
                          option.value === marital && "bg-purple-light/10",
                        )}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Field>
        </div>

        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className={cn(
            "mt-4 flex h-14 w-full items-center justify-center rounded-full font-body text-base font-semibold",
            canSave
              ? "bg-gradient-to-b from-peach to-purple-accent text-white"
              : "bg-purple-light/10 text-purple-light/20",
          )}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>

        <input
          ref={dateRef}
          type="date"
          value={dob}
          onChange={(e) => {
            setDob(e.target.value)
            if (e.target.value) setDateError(false)
          }}
          className="pointer-events-none absolute size-0 opacity-0"
        />
        <input
          ref={timeRef}
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="pointer-events-none absolute size-0 opacity-0"
        />
      </div>
    </EditProfileShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-body text-sm text-purple-light/70">{label}</span>
      {children}
    </div>
  )
}

function CalendarIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="4.5" y="5.5" width="15" height="14" rx="3.5" stroke="#9565FD" strokeWidth="1.5" />
      <path
        d="M8 3.5V7.5M16 3.5V7.5M4.5 9.5H19.5"
        stroke="#9565FD"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="7.5" stroke="#9565FD" strokeWidth="1.5" />
      <path d="M12 8.5V12L14.5 13.5" stroke="#9565FD" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={cn("transition-transform", open && "rotate-180")}
    >
      <path
        d="M7 10L12 15L17 10"
        stroke="#6F6391"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
